import { useSirenEntity } from '~/utility/BinSentry-ui-utility';
import _ from 'lodash';
import { DateTime } from 'luxon';

export function useOrderFasIntegrationState({ orderId, isActionable }) {
  const { entity: orderEntity, loading: loadingOrder } = useSirenEntity({ href: orderId });
  const integrationsHref = orderEntity?.getLinkByRel('https://api.binsentry.com/rel/integrations')?.href;
  const { entity: integrationsEntity, loading: loadingIntegrations } = useSirenEntity({ href: integrationsHref });
  const fasIntegrationsHref = integrationsEntity?.getSubEntity('https://api.binsentry.com/rel/fas-integration')?.href;
  const { entity: fasIntegrationsEntity, loading: loadingFas } = useSirenEntity({ href: fasIntegrationsHref });

  const mobileEventReferenceNumbers = fasIntegrationsEntity?.properties.mobileEventReferenceNumbers || [];
  const latestMobileOrderCreationHistory = _.last(fasIntegrationsEntity?.properties.mobileOrderCreationHistory || []);
  const { state, errors } = latestMobileOrderCreationHistory || {};
  const latestOrderPullHistory = _.last(fasIntegrationsEntity?.properties.orderPullHistory);

  const loading = loadingOrder || loadingIntegrations || loadingFas;
  let message;
  let resultState;
  if (!loading && latestMobileOrderCreationHistory && state !== 'success') {
    resultState = 'error';
    let sanitizedErrorMessage = errors?.[0] ? errors[0] : '';
    if (sanitizedErrorMessage.startsWith('FAS Integration')) {
      // There are three errors we see: those about missing Groups, those about missing/invalid _fas.binId, and those from FAS.
      // These last errors begin with `FAS Integration` and we can sanitize these to be `Error contacting FAS server. Consider reaching out to FAS support for assistance.`
      sanitizedErrorMessage = 'Error contacting FAS server. Consider reaching out to FAS support for assistance.';
    }
    message = `Creating an FAS mobile order failed${errors?.[0] ? `: ${sanitizedErrorMessage}` : ''}`;
  } else if (loading) {
    resultState = 'loading';
    message = '';
  } else if (!mobileEventReferenceNumbers.length && !fasIntegrationsEntity?.properties.poNumber) {
    resultState = 'not-synced';
    if (!orderId) {
      message = 'Mobile order will be created in FAS when the order is saved';
    } else if (['draft', 'ready'].includes(orderEntity?.properties.state)) {
      if (orderEntity.properties.type === 'request') {
        message = 'Mobile order cannot be created in FAS, confirm request to create';
      } else {
        message = 'Mobile order cannot be created in FAS, schedule order to create';
      }
    } else {
      message = 'Mobile order not created in FAS';
    }
  } else if (state === 'success') {
    resultState = 'success';
    message = `Last mobile order created in FAS: ${formatDate(latestMobileOrderCreationHistory.createdAt)}`;
    if (isActionable) {
      message += '\nAny updates will not be sent to FAS';
    }
  } else if (latestOrderPullHistory) {
    resultState = 'success-pull';
    message = `Order pulled from FAS: ${formatDate(latestOrderPullHistory.createdAt)}`;
    if (isActionable) {
      message += '\nAny updates will not be sent to FAS';
    }
  }

  return {
    mobileEventReferenceNumbers,
    fasIntegrationsEntity,
    state: resultState,
    message,
    loading,
  };
}

function formatDate(createdAt) {
  return DateTime.fromISO(createdAt).toLocaleString(_.omit(DateTime.DATETIME_FULL, 'timeZoneName'));
}
